import React, { useEffect, useState } from 'react';
import {
  Box,
  Stack,
  Paper,
  Button,
  Divider,
  Badge,
  useMediaQuery,
  useTheme,
  Typography,
  Popover,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Checkbox,
  IconButton,
  Tooltip,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import GavelIcon from '@mui/icons-material/Gavel';
import PieChartIcon from '@mui/icons-material/PieChart';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FilterListIcon from '@mui/icons-material/FilterList';
import FilterDropdown from './FilterDropdown';
import BudgetFilterContent from './BudgetFilterContent';
import {
  COLOR_PRIMARY_1,
  COLOR_PRIMARY_1_300,
  COLOR_PRIMARY_2_800,
  COLOR_PRIMARY_2_600,
  COLOR_PRIMARY_2_200,
} from 'src/utils/colors';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ViewWeekOutlinedIcon from '@mui/icons-material/ViewWeekOutlined';
import ColumnPicker from 'src/App/Dashboards/Market/Components/Filters/SavedMarketListPage/ColumnPicker';
import { useSelector } from 'react-redux';
import { User } from 'src/interfaces/iuser';
import { COLOR_PRIMARY, DEFAULT_SELECTED_COLUMNS_EXPLORER } from 'src/utils/const';
import ShowFullTableButton from 'src/App/Dashboards/Market/Components/Buttons/ShowFullTableButton';

// Define common styles for all buttons
const commonButtonSx = {
  justifyContent: 'flex-start',
  boxShadow: 'none',
};

// Define styles specific to the contained (active) state
const containedButtonSx = {
  backgroundColor: COLOR_PRIMARY_2_800,
  color: 'white',
  borderColor: COLOR_PRIMARY_2_800,
  boxShadow: 1,
  '&:hover': {
    backgroundColor: COLOR_PRIMARY_2_800,
    borderColor: COLOR_PRIMARY_2_800,
    boxShadow: 3,
  },
};

// Define styles specific to the outlined (inactive) state
const outlinedButtonSx = {
  borderColor: COLOR_PRIMARY_2_800,
  color: COLOR_PRIMARY_2_800,
  backgroundColor: 'transparent',
  '&:hover': {
    backgroundColor: 'rgba(66, 85, 99, 0.04)',
    borderColor: COLOR_PRIMARY_2_800,
    color: COLOR_PRIMARY_2_800,
  },
};

// Define Props Interface
interface OptionType {
  value: string;
  label: string;
  isState?: boolean;
  countryCode?: string;
  helperText?: string;
  isExpandable?: boolean;
  isChild?: boolean;
  parentValue?: string | null;
  level?: number;
}

interface FilterBarProps {
  selectedRegions: string[];
  regionOptions: OptionType[];
  handleRegionsChange: (val: string[]) => void;
  selectedMarketSizes: string[];
  handleLocationsChange: (val: string[]) => void;
  handleMarketSizesChange: (val: string[] | string) => void;
  countryOptions: OptionType[];
  marketSizeOptions: OptionType[];
  onFilterChangeAccessCheckMiddleware: (val: string | string[], actionFunction: any) => void;
  openSearchModal: () => void;
  selectedLocations: string[];
  isMobileScreen: boolean;
  OnBoardingSearchBox: any;
  setMarketSearchInfo: (val: { searchName: string; countryCode: string } | null) => void;
  isFreeUser: boolean;
  currency: string;
  handleSidebarClick?: any;
  handleSidebarListItemClick?: any;
  onExport: () => void;
  userPickedColumnFields: string[];
  onUserPickedFieldsChange: (fields: string[]) => void;
  showFullTable: boolean;
  setShowFullTable: React.Dispatch<React.SetStateAction<boolean>>;
}

const FilterBar: React.FC<FilterBarProps> = ({
  selectedRegions,
  handleRegionsChange,
  selectedMarketSizes,
  handleLocationsChange,
  handleMarketSizesChange,
  countryOptions,
  marketSizeOptions,
  onFilterChangeAccessCheckMiddleware,
  openSearchModal,
  isMobileScreen,
  selectedLocations,
  OnBoardingSearchBox,
  setMarketSearchInfo,
  isFreeUser,
  currency,
  handleSidebarClick,
  handleSidebarListItemClick,
  onExport,
  userPickedColumnFields,
  onUserPickedFieldsChange,
  showFullTable,
  setShowFullTable,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const user: User = useSelector((state: any) => state.dashboard.user);

  const [columnPickerOpen, setColumnPickerOpen] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
  const [selectedInvestorType, setSelectedInvestorType] = useState<string>('buy');

  // State for nested filter popovers
  const [regulationAnchor, setRegulationAnchor] = useState<null | HTMLElement>(null);
  const [budgetAnchor, setBudgetAnchor] = useState<null | HTMLElement>(null);
  const [marketSizeAnchor, setMarketSizeAnchor] = useState<null | HTMLElement>(null);
  const [expandedRegulationParents, setExpandedRegulationParents] = useState<string[]>([]);
  const [expandedMarketSizeParents, setExpandedMarketSizeParents] = useState<string[]>([]);

  useEffect(() => {
    const userStrategy = user.userStrategy?.toLowerCase();
    if (userStrategy === 'buy') {
      setSelectedInvestorType('buy');
    } else if (userStrategy === 'rent') {
      setSelectedInvestorType('rent');
    } else if (userStrategy === 'manage') {
      setSelectedInvestorType('manage');
    } else {
      setSelectedInvestorType('buy');
    }
  }, [user]);

  const handleApplyColumns = (newSelectedFields: string[]) => {
    onUserPickedFieldsChange(newSelectedFields);
    setColumnPickerOpen(false);
  };

  // Helper function to check if all ancestors are expanded
  const areAncestorsExpanded = (
    option: OptionType,
    allOptions: OptionType[],
    currentExpandedParents: string[]
  ): boolean => {
    let currentParentValue = option.parentValue;
    while (currentParentValue) {
      if (!currentExpandedParents.includes(currentParentValue)) {
        return false;
      }
      const parentOption = allOptions.find(o => o.value === currentParentValue);
      currentParentValue = parentOption ? parentOption.parentValue : null;
    }
    return true;
  };

  // Filter options based on expanded state
  const getFilteredOptions = (options: OptionType[], expandedParents: string[]) => {
    return options.filter(opt => {
      if (opt.level === 0) {
        return true;
      }
      return areAncestorsExpanded(opt, options, expandedParents);
    });
  };

  // Handle market size option selection
  const handleMarketSizeSelect = (optionValue: string) => {
    const newValue = Array.isArray(selectedMarketSizes)
      ? selectedMarketSizes.includes(optionValue)
        ? selectedMarketSizes.filter(v => v !== optionValue)
        : [...selectedMarketSizes, optionValue]
      : [optionValue];
    onFilterChangeAccessCheckMiddleware(newValue, handleMarketSizesChange);
  };

  // Handle expand/collapse for market size
  const handleMarketSizeExpand = (optionValue: string) => {
    setExpandedMarketSizeParents(prev =>
      prev.includes(optionValue) ? prev.filter(v => v !== optionValue) : [...prev, optionValue]
    );
  };

  const ToolbarSection = () => {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          justifyContent: isMobile ? 'flex-start' : 'flex-end',
          my: '10px',
        }}
      >
        <ShowFullTableButton showFullTable={showFullTable} setShowFullTable={setShowFullTable} />

        <Tooltip title="Customize Columns">
          <IconButton
            onClick={() => setColumnPickerOpen(true)}
            sx={{
              color: COLOR_PRIMARY_2_800,
              background: COLOR_PRIMARY_2_200 + '22',
              height: '40px',
              width: '40px',
              border: '1px solid rgba(0, 0, 0, 0.23)',
              borderRadius: 1,
              '&:hover': {
                backgroundColor: COLOR_PRIMARY_2_200 + '44',
              },
            }}
          >
            <ViewWeekOutlinedIcon />
          </IconButton>
        </Tooltip>

        <Button
          variant="outlined"
          onClick={onExport}
          startIcon={<FileDownloadOutlinedIcon />}
          sx={{
            color: COLOR_PRIMARY_2_800,
            background: COLOR_PRIMARY_2_200 + '22',
            fontWeight: 600,
            height: '40px',
            borderColor: 'rgba(0, 0, 0, 0.23)',
          }}
        >
          Export
        </Button>
      </Box>
    );
  };

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          overflowX: 'auto',
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        <Box sx={{ minWidth: 'max-content' }}>
          <Stack
            direction={isMobile ? 'column' : 'row'}
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <FilterDropdown
                label="Location"
                button={
                  <Button
                    variant={selectedLocations.length > 0 ? 'contained' : 'outlined'}
                    startIcon={<LocationOnIcon />}
                    endIcon={<ExpandMoreIcon />}
                    sx={{
                      ...commonButtonSx,
                      ...(selectedLocations.length > 0 ? containedButtonSx : outlinedButtonSx),
                    }}
                    disableElevation
                  >
                    Location
                  </Button>
                }
                options={countryOptions}
                value={selectedLocations}
                onChange={val => {
                  handleLocationsChange(val as string[]);
                  if (setMarketSearchInfo) {
                    setMarketSearchInfo(null);
                  }
                }}
                multiSelect
                selectedCountBadge
                placeholder="Search country or US state"
                onNoResultsAction={openSearchModal}
                OnBoardingSearchBox={OnBoardingSearchBox}
                setMarketSearchInfo={setMarketSearchInfo}
                isFreeUser={isFreeUser}
              />
              <FilterDropdown
                label="All Filters"
                button={
                  <Button
                    variant={selectedMarketSizes.length > 0 ? 'contained' : 'outlined'}
                    startIcon={<FilterListIcon />}
                    endIcon={<ExpandMoreIcon />}
                    sx={{
                      ...commonButtonSx,
                      ...(selectedMarketSizes.length > 0 ? containedButtonSx : outlinedButtonSx),
                    }}
                    disableElevation
                  >
                    All Filters
                  </Button>
                }
                options={[]}
                value={[]}
                onChange={() => {}}
                customContent={({ handleClose }) => {
                  const marketSizeFilteredOptions = getFilteredOptions(
                    marketSizeOptions,
                    expandedMarketSizeParents
                  );

                  return (
                    <Box sx={{ p: 2, minWidth: 300, maxWidth: 400 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 18, mb: 2 }}>
                        All Filters
                      </Typography>

                      <Box>
                        <Button
                          variant={selectedMarketSizes.length > 0 ? 'contained' : 'outlined'}
                          startIcon={<PieChartIcon />}
                          endIcon={<ExpandMoreIcon />}
                          onClick={e => setMarketSizeAnchor(e.currentTarget)}
                          sx={{
                            ...commonButtonSx,
                            ...(selectedMarketSizes.length > 0
                              ? containedButtonSx
                              : outlinedButtonSx),
                          }}
                          disableElevation
                          fullWidth
                        >
                          Market Size
                          {selectedMarketSizes.length > 0 && (
                            <Badge
                              badgeContent={selectedMarketSizes.length}
                              color="primary"
                              sx={{ ml: 2 }}
                            />
                          )}
                        </Button>
                        <Popover
                          open={Boolean(marketSizeAnchor)}
                          anchorEl={marketSizeAnchor}
                          onClose={() => setMarketSizeAnchor(null)}
                          anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'left',
                          }}
                          transformOrigin={{
                            vertical: 'top',
                            horizontal: 'left',
                          }}
                        >
                          <Box sx={{ p: 2, minWidth: 250, maxWidth: 300 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 18, mb: 1 }}>
                              Market Size
                            </Typography>
                            <List dense sx={{ maxHeight: 224, overflowY: 'auto' }}>
                              {marketSizeFilteredOptions.map(opt => {
                                const isSelected =
                                  Array.isArray(selectedMarketSizes) &&
                                  selectedMarketSizes.includes(opt.value);
                                const level = opt.level || 0;
                                const paddingLeft = 2 + level * 2;

                                return (
                                  <ListItemButton
                                    key={opt.value}
                                    dense
                                    onClick={() => {
                                      if (opt.isExpandable) {
                                        handleMarketSizeExpand(opt.value);
                                      } else {
                                        handleMarketSizeSelect(opt.value);
                                      }
                                    }}
                                    selected={isSelected}
                                    sx={{
                                      height: '40px !important',
                                      pl: paddingLeft,
                                      pr: opt.isExpandable ? 1 : 2,
                                      marginBottom: '10px',
                                    }}
                                  >
                                    <ListItemIcon sx={{ minWidth: 0, mr: 1.5 }}>
                                      <Checkbox
                                        edge="start"
                                        checked={isSelected}
                                        tabIndex={-1}
                                        disableRipple
                                        size="small"
                                        sx={{ padding: 0, marginRight: 1 }}
                                        onClick={e => {
                                          e.stopPropagation();
                                          handleMarketSizeSelect(opt.value);
                                        }}
                                      />
                                    </ListItemIcon>
                                    <ListItemText primary={opt.label} secondary={opt?.helperText} />
                                    {opt.isExpandable && (
                                      <IconButton
                                        edge="end"
                                        size="small"
                                        onClick={e => {
                                          e.stopPropagation();
                                          handleMarketSizeExpand(opt.value);
                                        }}
                                        sx={{
                                          transform: expandedMarketSizeParents.includes(opt.value)
                                            ? 'rotate(180deg)'
                                            : 'rotate(0deg)',
                                          transition: 'transform 0.2s',
                                        }}
                                      >
                                        {expandedMarketSizeParents.includes(opt.value) ? (
                                          <ExpandMoreIcon />
                                        ) : (
                                          <ChevronRightIcon />
                                        )}
                                      </IconButton>
                                    )}
                                  </ListItemButton>
                                );
                              })}
                            </List>
                          </Box>
                        </Popover>
                      </Box>
                    </Box>
                  );
                }}
                hideSearch={true}
              />
            </Stack>
            {!isMobile && ToolbarSection()}
          </Stack>
        </Box>
      </Paper>
      {isMobile && ToolbarSection()}

      {columnPickerOpen && (
        <ColumnPicker
          open={columnPickerOpen}
          onClose={() => setColumnPickerOpen(false)}
          initialSelectedColumns={userPickedColumnFields}
          onApplyColumns={handleApplyColumns}
          initialInvestorType={selectedInvestorType}
          onApplyInvestorType={setSelectedInvestorType}
          isExplorer={true}
        />
      )}
    </>
  );
};

export default FilterBar;
